import { AppConfig, ConfigEnvironment, Auth0Config, AdProvider } from "@/types/config.types";
import { getAdProvider } from "@/ads/getAdProvider";

function normalizeAdProvider(): AdProvider {
    return getAdProvider();
}

/** Default site id from existing Raptive manual tag; override with `REACT_APP_RAPTIVE_SITE_ID`. */
const RAPTIVE_SITE_ID_DEFAULT = "6998b43f1c0774780bdc7b2f";

/**
 * CONFIGURATION file to deploy the correct variables for each environment
 * new environment can be added to the scripts to the package.json file
 * for example:
 *   'start': 'REACT_APP_STAGE=local npm-run-all -p watch-css start-js',
 *   'build': 'npm run build-css && react-scripts build',
 *   'build-dev': 'REACT_APP_STAGE=dev react-scripts build',
 *   'build-prod': 'REACT_APP_STAGE=prod react-scripts build',
 *   'build-qa': 'REACT_APP_STAGE=qa react-scripts build',
 *
 * The common constant will have the common variables between the environments
 */

/**
 * The variables to use in ENV environment when is run locally
 */
const local: ConfigEnvironment = {
    API_URL: `http://localhost:${process.env.PORT || 5000}`,
    ONESIGNAL_ID: "63e60876-42b1-45bd-a887-310ac77212ba", // Defaults to staging OneSignal ID
    BASE_URL: "http://localhost:3000",
    LIVE_VARIANT_BASE_URL: "http://localhost:3000",
    MIXPANEL_TOKEN: null, // Optional for local development - Add your mixpanel project's token for dev works
    ADSENSE_CLIENT_ID: process.env.REACT_APP_ADSENSE_CLIENT_ID ?? "ca-pub-9011501733775673",
    LOGTAIL_SOURCE_TOKEN: null, //"6trFKUEw2UT84Gg9cNY2t5Pg", Uncomment to use Logtail in local development
    LOGTAIL_INGEST_HOST: null, //"https://s1611049.eu-nbg-2.betterstackdata.com", Uncomment to use Logtail in local development
};

/**
 * The variables to use in ENV environment when is run in STAGING environment
 */
const staging: ConfigEnvironment = {
    API_URL: "http://localhost:3000",
    ONESIGNAL_ID: "63e60876-42b1-45bd-a887-310ac77212ba", // OneSignal ID for staging
    BASE_URL: "https://priceme-staging-7b63d65502e9.herokuapp.com",
    LIVE_VARIANT_BASE_URL: "https://variant-test-staging-b913719c7bb5.herokuapp.com",
    MIXPANEL_TOKEN: "65553c4772ac0eecfa1e53c8f829e287",
    ADSENSE_CLIENT_ID: process.env.REACT_APP_ADSENSE_CLIENT_ID ?? "ca-pub-9011501733775673",
    LOGTAIL_SOURCE_TOKEN: "npJCkqthf2cog3M4XWPnCDYp",
    LOGTAIL_INGEST_HOST: "https://s1585599.eu-nbg-2.betterstackdata.com",
};

/**
 * The variables to use in PROD environment
 */
const prod: ConfigEnvironment = {
    API_URL: "http://localhost:3000",
    ONESIGNAL_ID: "a351a310-e06a-4db9-b112-8406b4c81795", // OneSignal ID for prod
    BASE_URL: "https://www.priceme.game",
    LIVE_VARIANT_BASE_URL: "https://www.priceme.game",
    MIXPANEL_TOKEN: "a4efd019848209a0477cf2910f9d2fd3",
    ADSENSE_CLIENT_ID: process.env.REACT_APP_ADSENSE_CLIENT_ID ?? "ca-pub-9011501733775673",
    LOGTAIL_SOURCE_TOKEN: "XuZ32ikXKGiAiLxq9sTm5ArV",
    LOGTAIL_INGEST_HOST: "https://s1583220.eu-nbg-2.betterstackdata.com",
};

/**
 * The common variables to use in any environment
 */
const common = {
    GOOGLE_MAPS_API_KEY: "AIzaSyBOgZ5nkY07RcZqf0ZYp9G0bde-EzPskKY",
    GOOGLE_MAP_ID: "472a1533198e7b01",
    DAILY_PROPERTIES: 3,
    CHALLENGE_PROPERTIES: 3,
    ADSENSE_GENERAL_AD_PLACEMENT_SLOT_ID: "9472500953", // AdSense slot ID for generic ad placements
    ADSENSE_PROPERTY_PHOTOS_SLOT_ID: "2454675818", // AdSense slot ID for photo scroll ads (every 10th photo)
    ADSENSE_PUZZLE_DETAILS_SLOT_ID: "7488985429", // AdSense slot ID for puzzle details page ad (puzzle details for mobile only)
    ADSENSE_PUZZLE_SIDEBAR_SLOT_ID: "9919460228", // AdSense sidebar ad slot ID for puzzle page (desktop only)
    ADSENSE_PUZZLE_FEED_SLOT_ID: "6398824090", // AdSense in-feed ad slot ID for puzzle feed
    ADSENSE_IN_FEED_LAYOUT_KEY: "+1p+rn+1v-2l-d", // AdSense in-feed ad layout key
    AUTH0: {
        domain: "dev-kb71sgmlh0p32db1.us.auth0.com",
        client_id: "mAd5gwcuDReAmdYcplS3RJudzCdeseWi",
    } as Auth0Config,
};

const config = (): AppConfig => {
    let envConfig: ConfigEnvironment;

    if (process.env.REACT_APP_STAGE === "production") {
        envConfig = prod;
    } else if (process.env.REACT_APP_STAGE === "staging") {
        envConfig = staging;
    } else {
        envConfig = local;
    }

    return {
        ...common,
        ...envConfig,
        AD_PROVIDER: normalizeAdProvider(),
        RAPTIVE_SITE_ID: (process.env.REACT_APP_RAPTIVE_SITE_ID || "").trim() || RAPTIVE_SITE_ID_DEFAULT,
        // CHALLENGE_PROPERTIES is commented out since challenge feature is disabled
        MAXIMUM_GUESS: common.DAILY_PROPERTIES /*  + common.CHALLENGE_PROPERTIES */,
    };
};

export default config();
